import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateOnPremSoundtrackAssetLanguageRequest } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremSoundtrackAssetLanguageRequest";
import { UpdateOnPremSoundtrackAssetLanguageResponse } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremSoundtrackAssetLanguageResponse";
import { ValidateOnPremSoundtrackAssetLanguageRequest } from "../../../../../../common/api/master-api/assets/details/ValidateOnPremSoundtrackAssetLanguageRequest";
import { useAssetsApi } from "../../../../../../common/api/master-api/assets/useAssetsApi";
import { OnPremAssetQueryKey } from "../../../common/OnPremAssetQueryKey";

export interface UseEditOnPremAsset {
    validateOnPremSoundtrackAssetLanguage: SprintMutationResult<
        ValidateOnPremSoundtrackAssetLanguageRequest,
        ValidationResult
    >;
    updateOnPremSoundtrackAssetLanguage: SprintMutationResult<
        UpdateOnPremSoundtrackAssetLanguageRequest,
        UpdateOnPremSoundtrackAssetLanguageResponse
    >;
}

export const useEditOnPremSoundtrackAssetLanguage = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditOnPremAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateOnPremSoundtrackAssetLanguage = useSprintMutation<
        ValidateOnPremSoundtrackAssetLanguageRequest,
        ValidationResult
    >({
        mutationFn: async ({ assetId, soundtrackLanguageId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate soundtrack language.");
            }

            return assetsApi.validateOnPremSoundtrackAssetLanguage({ assetId, soundtrackLanguageId });
        }
    });

    const updateOnPremSoundtrackAssetLanguage = useSprintMutation<
        UpdateOnPremSoundtrackAssetLanguageRequest,
        UpdateOnPremSoundtrackAssetLanguageResponse
    >({
        mutationFn: async ({ assetId, soundtrackLanguageId }: UpdateOnPremSoundtrackAssetLanguageRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update soundtrack language.");
            }

            return assetsApi.updateOnPremSoundtrackAssetLanguage({ assetId, soundtrackLanguageId });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [OnPremAssetQueryKey.OnPremAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateOnPremSoundtrackAssetLanguage,
        updateOnPremSoundtrackAssetLanguage
    };
};
